import { ADMIN_AUTH_ERROR, type AdminSessionResponse } from '@/contracts';

import { adminSession } from './session';
import { API_PREFIX, send, type AdminApiResult, type RequestOptions } from './http';

/**
 * One renewal at a time.
 *
 * Refresh tokens rotate, so two concurrent calls would each invalidate the
 * other's token and end the session — the same rule the mobile client's session
 * controller is built around. Every 401 joins the in-flight attempt rather than
 * starting its own, which matters because the console fires several requests per
 * screen and they all expire together.
 */
let renewal: Promise<boolean> | null = null;

export async function renew(): Promise<boolean> {
  renewal ??= (async () => {
    // No body: the token is the cookie, which the browser attaches and this code
    // cannot read. That is the point of it being httpOnly.
    const result = await send<AdminSessionResponse>(`${API_PREFIX}/admin/auth/refresh`, {
      method: 'POST',
      body: {},
      anonymous: true,
    });

    if (!result.ok) {
      adminSession.clear();
      return false;
    }

    adminSession.start(result.data);
    return true;
  })().finally(() => {
    renewal = null;
  });

  return renewal;
}

/**
 * Sends, and on a 401 renews once and sends again.
 *
 * The retry is marked so a second failure cannot loop: a token that was just
 * minted and is still refused means the session is genuinely over, not that the
 * clock drifted.
 */
export async function authenticated<T>(
  path: string,
  options: RequestOptions = {},
): Promise<AdminApiResult<T>> {
  const first = await send<T>(path, options);
  // Only a 401 is an expired token. A 404 or a 403 also reads `session_invalid` (see
  // `status` above), and renewing for one would rotate the refresh token for nothing.
  if (
    first.ok ||
    first.error !== ADMIN_AUTH_ERROR.SESSION_INVALID ||
    (first.status !== undefined && first.status !== 401)
  ) {
    return first;
  }

  return (await renew()) ? send<T>(path, options) : first;
}
